import { useQuery } from '@tanstack/react-query';
import type { AuthConfig, MeResponse } from '@seventide/types';
import { api, isApiError, rememberCsrfToken } from '../lib/api';
import { qk } from '../lib/queryClient';

/** GET /api/me without redirecting; resolves to `null` when signed out. */
export async function fetchMe(): Promise<MeResponse | null> {
  try {
    const me = await api.get<MeResponse>('/api/me', { redirectOn401: false });
    rememberCsrfToken(me.csrfToken);
    return me;
  } catch (e) {
    if (isApiError(e) && e.status === 401) return null;
    throw e;
  }
}

/** The signed-in user and their workspaces. `data === null` means signed out. */
export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: fetchMe, staleTime: 60_000 });
}

/** Public auth configuration (demo sign-in, passkeys). */
export function useAuthConfig() {
  return useQuery({
    queryKey: qk.authConfig,
    queryFn: () => api.get<AuthConfig>('/api/auth/config', { redirectOn401: false }),
    staleTime: 5 * 60_000,
  });
}
